import CheckIcon from '@mui/icons-material/Check';
import { Box, Chip } from '@mui/material';
import { useSilobagListContext } from '@siloreal:modules:private/silobag-list/config/context';
import ASSETS from '@siloreal:modules:private/silobag-list/config/extra/assets';
import UTILS from '@siloreal:modules:private/silobag-list/config/extra/utils';
import { getVriBadgeLabel, VriBadge } from '@siloreal:modules:private/silobag-list/config/ui/vri-badge';
import { getVrrBadgeLabel, VrrBadge } from '@siloreal:modules:private/silobag-list/config/ui/vrr-badge';
import { listCountrysidesService } from '@siloreal:shared:api/service/countryside/countryside-list';
import { listLotsService } from '@siloreal:shared:api/service/lot/lot-list';
import { listSelectableOrganizationsService } from '@siloreal:shared:api/service/organization/organization-selectable-list';
import { listSilobagHarvestsService } from '@siloreal:shared:api/service/silobag/silobag-harvest-list';
import { listSilobagProductsService } from '@siloreal:shared:api/service/silobag/silobag-product-list';
import { listSilobagSpeciesService } from '@siloreal:shared:api/service/silobag/silobag-species-list';
import {
  listTagSelectorService,
  type TagSelectorItem,
  type TagSelectorResponse,
} from '@siloreal:shared:api/service/tag/tag-selector';
import * as UI from '@siloreal:shared:ui';
import * as HOOKS from '@siloreal:shared:hooks';

export const FILTERS = {
  SELECTOR: {
    SPECIES: <SpeciesFilter key="silobag_species_type_id" />,
    PRODUCT: <ProductFilter key="silobag_product_type_id" />,
    HARVEST: <HarvestFilter key="silobag_harvest_id" />,
    ORGANIZATION: <OrganizationFilter key="organization_id" />,
    COMPANY: <CompanyFilter key="company_id" />,
    COUNTRYSIDE: <CountrysideFilter key="countryside_id" />,
    COUNTRYSIDE_ZONE: <CountrysideZoneFilter key="countryside_zone_id" />,
    LOT: <LotFilter key="lot_id" />,
    ALERT_LEVEL: <AlertLevelFilter key="color_id" />,
    TAG: <TagFilter key="tag_id" />,
  },
  MULTIBOX: {
    SILO_SAT: <SiloSatFilter key="sat_view_active" />,
    SILO_METRIX: <SiloMetrixFilter key="is_silo_metrix_active" />,
    INCONSISTENCIES: <InconsistenciesFilter key="inconsistencies" />,
  },
  CALENDAR: {
    CREATED_AT: <LifeCycleFilter key="created_at" />,
    CLOSURE_DATE: <LifeCycleFilter key="created_at" closed />,
    BAGGING_DATE: <BaggingDateFilter key="bagging_date" />,
  },
  RANGE: {
    INITIAL_HUMIDITY: <InitialHumidityFilter key="initial_humidity" />,
    REMAINING_STOCK: <RemainingStockFilter key="remaining_stock" />,
    TOTAL_EXTRACTED_WEIGHT: <TotalExtractedWeightFilter key="total_extracted_weight" />,
  },
} as const;

export const TABLE_FILTERS = {
  SILOS_PRO: {
    VRR: [
      FILTERS.SELECTOR.SPECIES,
      FILTERS.SELECTOR.PRODUCT,
      FILTERS.SELECTOR.HARVEST,
      FILTERS.SELECTOR.ORGANIZATION,
      FILTERS.SELECTOR.COMPANY,
      FILTERS.SELECTOR.COUNTRYSIDE,
      FILTERS.SELECTOR.COUNTRYSIDE_ZONE,
      FILTERS.SELECTOR.LOT,
      FILTERS.SELECTOR.ALERT_LEVEL,
      FILTERS.SELECTOR.TAG,
      FILTERS.MULTIBOX.SILO_SAT,
      FILTERS.MULTIBOX.SILO_METRIX,
      FILTERS.MULTIBOX.INCONSISTENCIES,
      FILTERS.CALENDAR.CREATED_AT,
      FILTERS.CALENDAR.BAGGING_DATE,
      FILTERS.RANGE.INITIAL_HUMIDITY,
      FILTERS.RANGE.REMAINING_STOCK,
      FILTERS.RANGE.TOTAL_EXTRACTED_WEIGHT,
    ],
    WITHOUT_VRR: [
      FILTERS.SELECTOR.SPECIES,
      FILTERS.SELECTOR.PRODUCT,
      FILTERS.SELECTOR.HARVEST,
      FILTERS.SELECTOR.ORGANIZATION,
      FILTERS.SELECTOR.COMPANY,
      FILTERS.SELECTOR.COUNTRYSIDE,
      FILTERS.SELECTOR.COUNTRYSIDE_ZONE,
      FILTERS.SELECTOR.LOT,
      FILTERS.SELECTOR.ALERT_LEVEL,
      FILTERS.SELECTOR.TAG,
      FILTERS.MULTIBOX.SILO_SAT,
      FILTERS.MULTIBOX.SILO_METRIX,
      FILTERS.MULTIBOX.INCONSISTENCIES,
      FILTERS.CALENDAR.CREATED_AT,
      FILTERS.CALENDAR.BAGGING_DATE,
      FILTERS.RANGE.INITIAL_HUMIDITY,
      FILTERS.RANGE.REMAINING_STOCK,
      FILTERS.RANGE.TOTAL_EXTRACTED_WEIGHT,
    ],
    CLOSED: [
      FILTERS.SELECTOR.SPECIES,
      FILTERS.SELECTOR.PRODUCT,
      FILTERS.SELECTOR.HARVEST,
      FILTERS.SELECTOR.ORGANIZATION,
      FILTERS.SELECTOR.COMPANY,
      FILTERS.SELECTOR.COUNTRYSIDE,
      FILTERS.SELECTOR.COUNTRYSIDE_ZONE,
      FILTERS.SELECTOR.LOT,
      FILTERS.SELECTOR.TAG,
      FILTERS.MULTIBOX.SILO_SAT,
      FILTERS.MULTIBOX.SILO_METRIX,
      FILTERS.CALENDAR.CLOSURE_DATE,
      FILTERS.CALENDAR.BAGGING_DATE,
      FILTERS.RANGE.INITIAL_HUMIDITY,
      FILTERS.RANGE.REMAINING_STOCK,
      FILTERS.RANGE.TOTAL_EXTRACTED_WEIGHT,
    ],
  },
  SILOBAG: {
    ACTIVE: [
      FILTERS.SELECTOR.SPECIES,
      FILTERS.SELECTOR.PRODUCT,
      FILTERS.SELECTOR.HARVEST,
      FILTERS.SELECTOR.ORGANIZATION,
      FILTERS.SELECTOR.COMPANY,
      FILTERS.SELECTOR.COUNTRYSIDE,
      FILTERS.SELECTOR.COUNTRYSIDE_ZONE,
      FILTERS.SELECTOR.LOT,
      FILTERS.SELECTOR.TAG,
      FILTERS.CALENDAR.CREATED_AT,
      FILTERS.CALENDAR.BAGGING_DATE,
      FILTERS.RANGE.INITIAL_HUMIDITY,
      FILTERS.RANGE.REMAINING_STOCK,
      FILTERS.RANGE.TOTAL_EXTRACTED_WEIGHT,
    ],
    CLOSED: [
      FILTERS.SELECTOR.SPECIES,
      FILTERS.SELECTOR.PRODUCT,
      FILTERS.SELECTOR.HARVEST,
      FILTERS.SELECTOR.ORGANIZATION,
      FILTERS.SELECTOR.COMPANY,
      FILTERS.SELECTOR.COUNTRYSIDE,
      FILTERS.SELECTOR.COUNTRYSIDE_ZONE,
      FILTERS.SELECTOR.LOT,
      FILTERS.SELECTOR.TAG,
      FILTERS.CALENDAR.CLOSURE_DATE,
      FILTERS.CALENDAR.BAGGING_DATE,
      FILTERS.RANGE.INITIAL_HUMIDITY,
      FILTERS.RANGE.REMAINING_STOCK,
      FILTERS.RANGE.TOTAL_EXTRACTED_WEIGHT,
    ],
    ARCHIVED: [
      FILTERS.SELECTOR.SPECIES,
      FILTERS.CALENDAR.BAGGING_DATE,
      FILTERS.RANGE.REMAINING_STOCK,
      FILTERS.SELECTOR.COUNTRYSIDE,
      FILTERS.SELECTOR.ORGANIZATION,
    ],
  },
};

type SelectorOption = {
  id: string;
  name: string;
};

type TagSelectorOption = SelectorOption & {
  tag: TagSelectorItem;
};

type SystemTagType = 'vrr' | 'vri';

type AlertLevelVisual = {
  backgroundColor: string;
  borderColor: string;
  accentColor: string;
  textColor: string;
  shortLabel: string | null;
};

const ALERT_LEVEL_VISUAL_BY_ID: Partial<Record<string, AlertLevelVisual>> = {
  '2': {
    backgroundColor: '#d32f2f',
    borderColor: '#982222',
    accentColor: '#ad2727',
    textColor: '#ffffff',
    shortLabel: 'MOV',
  },
  '4': {
    backgroundColor: '#ed6c02',
    borderColor: '#ab4e01',
    accentColor: '#c25802',
    textColor: '#ffffff',
    shortLabel: 'GR',
  },
  '3': {
    backgroundColor: '#ffeb3b',
    borderColor: '#b8a92a',
    accentColor: '#d1c130',
    textColor: '#1a1612',
    shortLabel: 'LEV',
  },
  '1': {
    backgroundColor: '#6baa00',
    borderColor: '#4d7a00',
    accentColor: '#588b00',
    textColor: '#ffffff',
    shortLabel: null,
  },
};

const formatPercentageRangeValue = (value: number): string => `${value}%`;
const formatKilogramRangeValue = (value: number): string => `${value.toLocaleString('es-AR')} Kg`;

async function listSelectableCompaniesService(organizationId?: number): Promise<SelectorOption[]> {
  const countrysides = await listCountrysidesService({
    query: { organization_id: organizationId },
  });
  const companyById = new Map<string, SelectorOption>();

  countrysides.forEach((countryside) => {
    const company = countryside.companies;
    if (!company) return;

    companyById.set(String(company.id), { id: String(company.id), name: company.name });
  });

  return Array.from(companyById.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function getSystemTagType(tag: TagSelectorItem): SystemTagType | null {
  if (tag.scope.value !== 'system') return null;
  if (UTILS.is.vrr.ok(tag) || UTILS.is.vrr.not_ok(tag)) return 'vrr';
  if (UTILS.extract.level.vri(tag) !== null) return 'vri';
  return null;
}

function isVrrListSystemTag(tag: TagSelectorItem): boolean {
  if (tag.scope.value !== 'system') return false;

  return (
    getSystemTagType(tag) === 'vrr' ||
    UTILS.is.tag.insurance.active(tag) ||
    UTILS.is.tag.insurance.expired(tag) ||
    UTILS.is.tag.warranty.active(tag) ||
    UTILS.is.tag.warranty.expired(tag)
  );
}

function TagOption({ option }: { option: TagSelectorOption }) {
  const { tag } = option;
  const systemTagType = getSystemTagType(tag);

  if (systemTagType) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        {systemTagType === 'vrr' ? <VrrBadge ok={UTILS.is.vrr.ok(tag)} /> : <VriBadge tag={tag} />}
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
          {option.name}
        </Box>
      </Box>
    );
  }

  return (
    <Chip
      icon={
        tag.image_url ? (
          <Box
            component="img"
            src={tag.image_url}
            alt=""
            sx={{ width: 14, height: 14, ml: 0.5, objectFit: 'contain' }}
          />
        ) : undefined
      }
      label={option.name}
      size="small"
      sx={{
        bgcolor: tag.bg_color,
        color: tag.text_color,
        fontWeight: 600,
        borderRadius: '6px',
        pointerEvents: 'none',
      }}
    />
  );
}

function AlertLevelOption({ option }: { option: SelectorOption }) {
  const visual = ALERT_LEVEL_VISUAL_BY_ID[option.id];

  if (!visual) return option.name;

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 50,
          height: 24,
          display: 'inline-flex',
          alignItems: 'stretch',
          flexShrink: 0,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: visual.borderColor,
          borderRadius: '5px',
          backgroundColor: visual.backgroundColor,
          boxShadow: '0 1px 2px rgba(22, 28, 20, 0.08), inset 0 1px 0 rgba(255,255,255,0.35)',
        }}
      >
        <Box component="span" sx={{ width: 3, flexShrink: 0, backgroundColor: visual.accentColor }} />
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: visual.textColor,
            fontFamily: '"Roboto Mono", "SF Mono", monospace',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.05em',
            lineHeight: 1,
          }}
        >
          {visual.shortLabel ?? <CheckIcon sx={{ fontSize: 16 }} />}
        </Box>
      </Box>
      <span>{option.name}</span>
    </Box>
  );
}

function ServiceFilterIcon({ src, alt }: { src: string; alt: string }) {
  return <Box component="img" src={src} alt={alt} sx={{ width: 18, height: 18, display: 'block' }} />;
}

function SpeciesFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.collection.serviceSelector
      tid="silobag-list-filter-species"
      name="silobag_species_type_id"
      label={i18n.t('temp.common.specie')}
      multiple
      service={{
        fn: listSilobagSpeciesService,
        key: ['silobag-species-list'],
        callback: {
          dataAccess: (species) => species.map((s) => ({ id: String(s.id), name: s.name })),
        },
      }}
    />
  );
}

function ProductFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.collection.serviceSelector
      tid="silobag-list-filter-product"
      name="silobag_product_type_id"
      label={i18n.t('temp.common.product')}
      multiple
      service={{
        fn: listSilobagProductsService,
        key: ['silobag-products-list'],
        callback: {
          dataAccess: (products) =>
            products.map((product) => ({ id: String(product.id), name: product.name })),
        },
      }}
    />
  );
}

function HarvestFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.collection.serviceSelector
      tid="silobag-list-filter-harvest"
      name="silobag_harvest_id"
      label={i18n.t('temp.common.harvest')}
      multiple
      service={{
        fn: listSilobagHarvestsService,
        key: ['silobag-harvests-list'],
        callback: {
          dataAccess: (harvests) =>
            harvests.map((harvest) => ({ id: String(harvest.id), name: harvest.name })),
        },
      }}
    />
  );
}

function AlertLevelFilter() {
  const i18n = HOOKS.useLocale();
  const options: SelectorOption[] = [
    { id: '2', name: i18n.t('temp.common.alert_level_movement') },
    { id: '4', name: i18n.t('temp.common.alert_level_quality_serious') },
    { id: '3', name: i18n.t('temp.common.alert_level_quality_min') },
    { id: '1', name: i18n.t('temp.common.alert_level_optimum') },
  ];

  return (
    <UI.filter.field.selector
      tid="silobag-list-filter-alert-level"
      name="color_id"
      label={i18n.t('temp.common.alert_level')}
      options={options}
      renderOption={(option) => (typeof option === 'string' ? option : <AlertLevelOption option={option} />)}
    />
  );
}

function OrganizationFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.collection.serviceSelector
      tid="silobag-list-filter-organization"
      name="organization_id"
      label={i18n.t('temp.common.organization')}
      multiple
      service={{
        fn: listSelectableOrganizationsService,
        key: ['selectable-organization-list'],
        callback: {
          dataAccess: (organizations) =>
            organizations.map((organization) => ({
              id: String(organization.id),
              name: organization.name,
            })),
        },
      }}
    />
  );
}

function CompanyFilter() {
  const i18n = HOOKS.useLocale();

  const {
    organization: { current: organizationId },
  } = useSilobagListContext();

  return (
    <UI.collection.serviceSelector
      tid="silobag-list-filter-company"
      name="company_id"
      label={i18n.t('temp.common.company')}
      multiple
      service={{
        fn: () => listSelectableCompaniesService(organizationId),
        key: ['selectable-company-list', organizationId],
      }}
    />
  );
}

function CountrysideFilter() {
  const {
    organization: { current: organizationId },
  } = useSilobagListContext();

  const i18n = HOOKS.useLocale();

  return (
    <UI.collection.serviceSelector
      tid="silobag-list-filter-countryside"
      name="countryside_id"
      label={i18n.t('temp.common.countryside')}
      multiple
      service={{
        fn: () => listCountrysidesService({ query: { organization_id: organizationId } }),
        key: ['countryside-list', organizationId],
        callback: {
          dataAccess: (countrysides) =>
            countrysides.map((countryside) => ({ id: String(countryside.id), name: countryside.name })),
        },
      }}
    />
  );
}

function CountrysideZoneFilter() {
  const {
    organization: { current: organizationId },
  } = useSilobagListContext();
  const i18n = HOOKS.useLocale();

  return (
    <UI.collection.serviceSelector
      tid="silobag-list-filter-countryside-zone"
      name="countryside_zone_id"
      label={i18n.t('temp.common.countryside_zone')}
      service={{
        fn: () => listCountrysidesService({ query: { organization_id: organizationId } }),
        key: ['countryside-list', organizationId],
        callback: {
          dataAccess: (countrysides) => {
            const zoneById = new Map<string, SelectorOption>();

            countrysides.forEach(({ countryside_zone: zone }) => {
              if (!zone) return;

              zoneById.set(String(zone.id), { id: String(zone.id), name: zone.name });
            });

            return Array.from(zoneById.values()).sort((a, b) => a.name.localeCompare(b.name));
          },
        },
      }}
    />
  );
}

function LotFilter() {
  const {
    organization: { current: organizationId },
  } = useSilobagListContext();

  const i18n = HOOKS.useLocale();

  return (
    <UI.collection.serviceSelector
      tid="silobag-list-filter-lot"
      name="lot_id"
      label={i18n.t('temp.common.lot')}
      multiple
      service={{
        fn: () => listLotsService({ query: { id: organizationId } }),
        key: ['lot-list', organizationId],
        callback: {
          dataAccess: (lots) => lots.map((lot) => ({ id: String(lot.id), name: lot.name })),
        },
      }}
    />
  );
}

function TagFilter() {
  const {
    state: { current: listState },
    organization: { current: organizationId },
  } = useSilobagListContext();
  const i18n = HOOKS.useLocale();
  const systemTagType: SystemTagType | null =
    listState.view !== 'silospro'
      ? null
      : listState.tab === 'vrr'
        ? 'vrr'
        : listState.tab === 'no-vrr'
          ? 'vri'
          : null;
  const isClosedTab = listState.tab === 'closed';
  const tagSelectorQuery = { organization_id: organizationId, active: !isClosedTab };

  return (
    <UI.collection.serviceSelector<TagSelectorOption, TagSelectorResponse>
      tid="silobag-list-filter-tag"
      name="tag_id"
      label={i18n.t('temp.common.tags')}
      multiple
      groupBy={(option) =>
        option.tag.scope.value === 'system'
          ? i18n.t('temp.common.system_tags')
          : i18n.t('temp.common.organization_tags')
      }
      renderOption={(option) => <TagOption option={option} />}
      service={{
        fn: () => listTagSelectorService({ query: tagSelectorQuery }),
        key: ['tag-selector', organizationId, isClosedTab, systemTagType, i18n.locale],
        callback: {
          dataAccess: ({ error, response }) => {
            if (error) throw new Error('No se pudieron obtener las etiquetas de la organización.');

            return response
              .filter((tag) => {
                if (tag.scope.value === 'organization') return true;
                if (systemTagType === 'vrr') return isVrrListSystemTag(tag);
                return systemTagType === 'vri' && getSystemTagType(tag) === 'vri';
              })
              .map((tag) => {
                const currentSystemTagType = getSystemTagType(tag);
                const translatedName =
                  tag.translations.find((translation) => translation.language === i18n.locale)?.value ||
                  tag.label;

                if (currentSystemTagType === 'vrr') {
                  return {
                    id: String(tag.id),
                    name: getVrrBadgeLabel(UTILS.is.vrr.ok(tag), i18n.t),
                    tag,
                  };
                }

                if (currentSystemTagType === 'vri') {
                  return {
                    id: String(tag.id),
                    name: getVriBadgeLabel(tag, i18n.t) ?? translatedName,
                    tag,
                  };
                }

                return { id: String(tag.id), name: translatedName, tag };
              });
          },
        },
      }}
    />
  );
}

const getBooleanFilterFieldProps = () => ({
  options: [
    { id: 'true', name: HOOKS.useLocale.getState().t('temp.common.yes') },
    { id: 'false', name: HOOKS.useLocale.getState().t('temp.common.no') },
  ],
  exclusive: true,
  direction: 'horizontal' as const,
  pushOptionsToEnd: true,
});

function SiloSatFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.filter.field.multibox
      tid="silobag-list-filter-silosat"
      name="sat_view_active"
      label={i18n.t('temp.common.silosat')}
      labelIcon={<ServiceFilterIcon src={ASSETS.SERVICES.SILO_SAT} alt="SiloSat" />}
      {...getBooleanFilterFieldProps()}
    />
  );
}

function SiloMetrixFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.filter.field.multibox
      tid="silobag-list-filter-silometrix"
      name="is_silo_metrix_active"
      label={i18n.t('temp.common.silometrix')}
      labelIcon={<ServiceFilterIcon src={ASSETS.SERVICES.SILO_METRIX} alt="SiloMetrix" />}
      {...getBooleanFilterFieldProps()}
    />
  );
}

function InconsistenciesFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.filter.field.multibox
      tid="silobag-list-filter-inconsistencies"
      name="inconsistencies"
      label={i18n.t('temp.common.inconsistencies')}
      labelIcon={ASSETS.INCONSISTENCIES}
      {...getBooleanFilterFieldProps()}
    />
  );
}

function LifeCycleFilter({ closed = false }: { closed?: boolean }) {
  const i18n = HOOKS.useLocale();

  return (
    <UI.filter.field.calendar
      tid={closed ? 'silobag-list-filter-closure-date' : 'silobag-list-filter-created-at'}
      name="created_at"
      label={closed ? i18n.t('temp.common.closure_date') : i18n.t('temp.private.list.common.register_date')}
      size="large"
    />
  );
}

function BaggingDateFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.filter.field.calendar
      tid="silobag-list-filter-bagging-date"
      name="bagging_date"
      label={i18n.t('temp.common.bagging_date')}
      size="large"
    />
  );
}

function InitialHumidityFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.filter.field.range
      tid="silobag-list-filter-initial-humidity"
      name="initial_humidity"
      label={i18n.t('temp.common.bagging_humidity')}
      min={0}
      max={100}
      step={1}
      size="large"
      formatValue={formatPercentageRangeValue}
    />
  );
}

function RemainingStockFilter() {
  const i18n = HOOKS.useLocale();

  return (
    <UI.filter.field.range
      tid="silobag-list-filter-remaining-stock"
      name="remaining_stock"
      label={i18n.t('temp.common.stock_in_field')}
      min={0}
      max={1000000}
      step={1000}
      size="large"
      formatValue={formatKilogramRangeValue}
    />
  );
}

function TotalExtractedWeightFilter() {
  const i18n = HOOKS.useLocale();
  return (
    <UI.filter.field.range
      tid="silobag-list-filter-total-extracted-weight"
      name="total_extracted_weight"
      label={i18n.t('temp.common.total_extracted')}
      min={0}
      max={1000000}
      step={1000}
      size="large"
      formatValue={formatKilogramRangeValue}
    />
  );
}
